/**
 * UTM attribution across new-tab boundaries.
 *
 * AppTrackingContext captures utm_* params from the URL on app mount and keeps
 * them in per-tab sessionStorage (under the user_* keys in ParamToKey);
 * AxiosInterceptor then stamps them on every tracking event, up to and
 * including hotel_complete_buy.
 *
 * On desktop the hotel list and the offers page open the shop in a new tab, and
 * a new tab starts with empty sessionStorage — so attribution stopped at the
 * list. Links that open a new tab carry the stored params in the URL instead,
 * and the new tab's AppTrackingContext re-captures them on mount. This mirrors
 * how `offer_source` bridges entry_source into the new-tab shop.
 */
import {
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
  USER_SOURCE_KEY,
} from "@hopper-b2b/utilities";

const UTM_PARAM_TO_STORAGE_KEY = {
  utm_source: USER_SOURCE_KEY,
  utm_medium: USER_MEDIUM_KEY,
  utm_campaign: USER_CAMPAIGN_KEY,
  utm_content: USER_CONTENT_KEY,
} as const;

export type UtmParam = keyof typeof UTM_PARAM_TO_STORAGE_KEY;

/** The UTM params captured on funnel entry for this tab, keyed by utm_* name. */
export const getStoredUtmParams = (): Partial<Record<UtmParam, string>> => {
  const stored: Partial<Record<UtmParam, string>> = {};
  try {
    for (const [param, storageKey] of Object.entries(
      UTM_PARAM_TO_STORAGE_KEY
    )) {
      const value = sessionStorage.getItem(storageKey);
      if (value) stored[param as UtmParam] = value;
    }
  } catch {
    // sessionStorage can be unavailable (private mode); this is best-effort.
  }
  return stored;
};

/**
 * Copy this tab's stored UTM params onto `params` so a link that opens in a new
 * tab hands its attribution over. Mutates and returns `params`. Params already
 * on the URL win, so a tag the current URL carries is never overwritten.
 */
export const forwardUtmParams = (params: URLSearchParams): URLSearchParams => {
  for (const [param, value] of Object.entries(getStoredUtmParams())) {
    if (!params.has(param)) params.set(param, value);
  }
  return params;
};
